// @file Composable for slideshow styles
// Update tailwind.config.js if this file is moved/renamed so that tailwind generates CSS for the classes here

import {
  isSmallerThanDesktop,
  isSmallerThanDesktop3XL,
  isSmallerThanDesktopBig,
  isSmallerThanTabletPortrait,
} from '@@/bits/window'
import { useSlideshowStore } from '@@/pinia/slideshow'
import { useSurfaceStore } from '@@/pinia/surface'
import { storeToRefs } from 'pinia'
import type { Ref } from 'vue'
import { computed, onMounted, onUnmounted, ref } from 'vue'

interface QRCodeStyle {
  baseDimension: number
  border: number
}

const qrCodeDefaultStyles = (): QRCodeStyle => {
  if (isSmallerThanTabletPortrait()) {
    return {
      baseDimension: 24,
      border: 2,
    }
  }
  if (isSmallerThanDesktop()) {
    return {
      baseDimension: 48,
      border: 4,
    }
  }
  if (isSmallerThanDesktopBig()) {
    return {
      baseDimension: 64,
      border: 5,
    }
  }
  if (isSmallerThanDesktop3XL()) {
    return {
      baseDimension: 96,
      border: 8,
    }
  }
  return {
    baseDimension: 120,
    border: 10,
  }
}

interface SlideshowStyles {
  hasLightBackgroundLuminance: Ref<boolean>
  hasDarkBackgroundLuminance: Ref<boolean>
  primaryColorStyle: Ref<string>
  accentColorStyle: Ref<string>
  textColorClass: Ref<{
    'text-dark-text-100': boolean
    'text-light-text-100': boolean
  }>
  qrCodeBaseStyles: Ref<QRCodeStyle>
  shouldRemoveBackground: Ref<boolean>
  backgroundLuminanceBasedTailwindClasses: Ref<Record<string, boolean>>
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function useSlideshowStyles(): SlideshowStyles {
  const { backgroundLuminance, skinPrimaryColorAsRGB, skinAccentColorAsRGB } = storeToRefs(useSurfaceStore())
  const { shouldRemoveBackground } = storeToRefs(useSlideshowStore())

  const hasLightBackgroundLuminance = computed(() =>
    shouldRemoveBackground.value ? true : backgroundLuminance.value === 'light',
  )
  const hasDarkBackgroundLuminance = computed(() =>
    shouldRemoveBackground.value ? false : backgroundLuminance.value === 'dark',
  )
  const primaryColorStyle = computed(() => (shouldRemoveBackground.value ? '#FFFFFF' : skinPrimaryColorAsRGB.value))
  const accentColorStyle = computed(() => (shouldRemoveBackground.value ? '#000000' : skinAccentColorAsRGB.value))
  const textColorClass = computed(() => ({
    'text-dark-text-100': hasLightBackgroundLuminance.value,
    'text-light-text-100': hasDarkBackgroundLuminance.value,
  }))

  const backgroundLuminanceBasedTailwindClasses = computed(() => {
    return {
      // Base
      'bg-grey-1000 bg-opacity-5': !hasDarkBackgroundLuminance.value,
      'bg-white-100 bg-opacity-10': hasDarkBackgroundLuminance.value,
      'text-dark-text-200': !hasDarkBackgroundLuminance.value,
      'text-light-text-200': hasDarkBackgroundLuminance.value,

      // Hovered
      'hover-hover:hover:bg-opacity-10': !hasDarkBackgroundLuminance.value,
      'hover-hover:hover:bg-opacity-15': hasDarkBackgroundLuminance.value,

      // Disabled
      'aria-disabled:!bg-highway-400': !hasDarkBackgroundLuminance.value,
      'aria-disabled:!bg-white-disabled': hasDarkBackgroundLuminance.value,
    }
  })

  const qrCodeBaseStyles = ref(qrCodeDefaultStyles())

  const resetStyles = (): void => {
    qrCodeBaseStyles.value = qrCodeDefaultStyles()
  }

  onMounted(() => {
    window.addEventListener('resize', resetStyles)
  })

  onUnmounted(() => {
    window.removeEventListener('resize', resetStyles)
  })

  return {
    hasLightBackgroundLuminance,
    hasDarkBackgroundLuminance,
    primaryColorStyle,
    accentColorStyle,
    textColorClass,
    qrCodeBaseStyles,
    shouldRemoveBackground,
    backgroundLuminanceBasedTailwindClasses,
  }
}
